import {
  type LogOverviewRow,
  useBulkRetry,
} from '@/api/hooks/useIntegrationLogs'
import { RequireScope } from '@/components/RequireScope'
import { GraviButton } from '@gravitate-js/excalibrr'
import { Popconfirm, Tooltip, message } from 'antd'
import type { ReactNode } from 'react'
import { MAX_BULK_RETRY_ORDERS, eligibleOrderRows } from './bulkRetrySelection'

export function bulkRetryLabel(orders: number): string {
  return `Bulk Retry ${orders} Order${orders === 1 ? '' : 's'}`
}

interface BulkRetryConfirmProps {
  logGroupIds: string[]
  description: string
  emptyTooltip?: string
  onStarted: () => void
  renderButton: (state: { disabled: boolean; loading: boolean }) => ReactNode
}

export function BulkRetryConfirm({
  logGroupIds,
  description,
  emptyTooltip = '',
  onStarted,
  renderButton,
}: BulkRetryConfirmProps) {
  const bulkRetry = useBulkRetry()

  const count = logGroupIds.length
  const overLimit = count > MAX_BULK_RETRY_ORDERS
  const disabled = count === 0 || overLimit || bulkRetry.isPending

  const tooltip = overLimit
    ? `Bulk retry is limited to ${MAX_BULK_RETRY_ORDERS} orders at a time. ${count} selected.`
    : count === 0
      ? emptyTooltip
      : ''

  const handleConfirm = () => {
    bulkRetry.mutate(
      { body: { log_group_ids: logGroupIds } },
      {
        onSuccess: (job) => {
          if (job.total === 0) {
            message.info('Nothing retryable in that selection')
          } else {
            message.success(
              `Bulk retry queued: ${job.total} item${job.total === 1 ? '' : 's'}`,
            )
          }
          onStarted()
        },
      },
    )
  }

  const button = renderButton({ disabled, loading: bulkRetry.isPending })

  return (
    <RequireScope scope='crossroads.retry'>
      {disabled ? (
        <Tooltip title={tooltip}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        <Popconfirm
          title={bulkRetryLabel(count)}
          description={description}
          okText='Retry'
          cancelText='Cancel'
          onConfirm={handleConfirm}
        >
          {button}
        </Popconfirm>
      )}
    </RequireScope>
  )
}

export function BulkRetryButton({
  selectedRows,
  onStarted,
}: { selectedRows: LogOverviewRow[]; onStarted: () => void }) {
  const orderRows = eligibleOrderRows(selectedRows)
  if (orderRows.length === 0) return null

  return (
    <BulkRetryConfirm
      logGroupIds={orderRows.map((row) => row.id)}
      description="Crossroads will retry failed events on the orders you've selected."
      onStarted={onStarted}
      renderButton={({ disabled, loading }) => (
        <GraviButton
          size='small'
          appearance='outline'
          color='blue'
          disabled={disabled}
          loading={loading}
        >
          {bulkRetryLabel(orderRows.length)}
        </GraviButton>
      )}
    />
  )
}
